import { useQuery } from '@apollo/client'
import { Carousel } from '@mantine/carousel'
import { Stack, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import { Entry } from 'contentful'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { explorePageActive } from '@/constants/blockchain'
import { useErrorMessages } from '@/constants/content'
import useSessionStorage from '@/hooks/useSessionStorage'
import { FEATURED_COLLECTIONS_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'
import { GetCollectionsByIds, QUERY_GET_COLLECTIONS_BY_IDS } from '@/schemas/collections.schema'
import { CollectionWithTotal } from '@/schemas/types'
import { ErrorMessageDisplay, shuffleArray } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'
import ErrorMessage from '../ErrorMessage'
import CollectionsCard from '../Items/CollectionsCard'
import Loading from '../Loading'

const FeaturedCollections: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const errorMessages = useErrorMessages()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<ErrorMessageDisplay>()
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const [featuredCollectionsContent, setFeaturedCollectionsContent] = useSessionStorage<Entry[]>(
    `featuredCollections`,
    []
  )

  const [collectionsIds, setCollectionsIds] = useState<string[]>([])
  const [sortedCollections, setSortedCollections] = useState<CollectionWithTotal[]>([])

  // Carousel state management
  const [slideNumber, setSlideNumber] = useState(0)
  const handleSlideChange = useCallback((index: number) => {
    setSlideNumber(index)
  }, [])

  useEffect(() => {
    if (!featuredCollectionsContent.length && !hasFetched) {
      setIsLoading(true)
      client
        .getEntries({ content_type: FEATURED_COLLECTIONS_CONTENT_TYPE })
        .then((response: any) => {
          setFeaturedCollectionsContent(response.items)
          if (response.items.length < 1) {
            setHasFetched(true)
          }
        })
        .catch((e: any) => {
          setError({
            message: errorMessages.cmsConnection,
            details: e.message,
          })
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [featuredCollectionsContent, setFeaturedCollectionsContent, hasFetched])

  const { data } = useQuery<GetCollectionsByIds>(QUERY_GET_COLLECTIONS_BY_IDS, {
    variables: {
      ids: collectionsIds,
    },
  })

  // get ids array from contentful
  useEffect(() => {
    if (featuredCollectionsContent.length > 0) {
      const newIds = featuredCollectionsContent.map(
        (item: Entry) => item.fields.collectionID as string
      )
      setCollectionsIds(newIds)
    }
  }, [featuredCollectionsContent])

  // make sure the nfts are sorted in the same order as the ids, since graphql doesn't guarantee order
  useEffect(() => {
    if (data) {
      const sortedData = collectionsIds
        // map the ids to the nfts
        .map(id => data.collection.find(nft => nft.id === id))
        // filter out the undefined values
        .filter(nft => nft !== undefined)
      if (sortedData.length > 0) {
        const shuffledCollections = shuffleArray(sortedData)
        setSortedCollections(shuffledCollections)
      }
    }
  }, [data, collectionsIds])

  const handleViewAll = useCallback(() => {
    navigate('/explore/collections')
  }, [navigate])

  return (
    featuredCollectionsContent.length > 0 && (
      <Stack style={{ gap: '30px' }}>
        <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
          <Title order={isMobile ? 3 : 2}>
            {t(`components.featuredCollections.title`, 'Content Collections')}
          </Title>
          {explorePageActive && (
            <RootButton secondary style1={false} onClick={handleViewAll}>
              {t(`components.featuredCreators.viewAll`, 'View All')}
            </RootButton>
          )}
        </Stack>
        {isLoading && <Loading size="300px" />}
        {error && <ErrorMessage message={error.message} details={error.details} />}
        {!isLoading && !error && (
          <Stack
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Carousel
              slideSize="0%"
              previousControlIcon={<IconCaretLeftFilled />}
              nextControlIcon={<IconCaretRightFilled />}
              slidesToScroll="auto"
              onSlideChange={handleSlideChange}
            >
              {collectionsIds.length > 0 &&
                collectionsIds.map(
                  (collection, index) =>
                    sortedCollections[index] &&
                    // only load the items that are in the current slideshow window
                    // TODO: make this dynamic based on the screen size (currently 7 item by default)
                    (slideNumber + 1) * 7 > index && (
                      <Carousel.Slide
                        key={`${collection}${String(index)}`}
                        style={{ marginTop: '10px' }}
                      >
                        <CollectionsCard collection={sortedCollections[index]} />
                      </Carousel.Slide>
                    )
                )}
            </Carousel>
          </Stack>
        )}
      </Stack>
    )
  )
}

export default FeaturedCollections
